import { MRT_ColumnDef, useMaterialReactTable } from 'material-react-table';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { Box, IconButton, Slide, Tooltip } from '@mui/material';
import { createFilterItems } from 'src/presentations/components/molecules/table/columnHeader';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { UserDto } from 'src/generated/services/TFinancialApi';
import { UserFilters, useUpsertUser, useUserList, useUsersMetadata } from 'src/use-cases/settings/useUsers';

import Button from 'src/presentations/components/atom/button';
import { useEnhancedTable } from 'src/presentations/hooks/useEnhancedTable';
import { useTableEntityHandler } from 'src/presentations/hooks/useTableEntityHandler';
import { SettingsBaseTable } from 'src/presentations/components/molecules/settings/table/BaseTable';
import { createTableConfig } from 'src/presentations/utils/tableConfigUtils';
import { SettingsMain } from '../SettingsMain';
import { UserDetail } from './UserDetail';
import { SettingsColumnHeader } from '../SettingsColumnHeader';

const firstCellStyles = {
  boxShadow: '-4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
  borderLeft: '1px solid #E5E7EB',
  width: '120px',
  padding: '0 10px 0 10px !important',

  '& .MuiBox-root': {
    gap: '0!important',
    width: '90px',
    justifyContent: 'center',

    '& svg': {
      width: '20px!important',
    },
  },
};

interface UserTableProps {
  searchQuery?: string;
}

// Local interface for component state - maps to API filters
interface UserFilterState {
  firstNames: string[];
  lastNames: string[];
  emails: string[];
  legalEntityCodes: string[];
}

export const ProfileMain: FC<UserTableProps> = (props): JSX.Element => {
  const { searchQuery } = props;
  const [filterState, setFilterState] = useState<UserFilterState>({
    firstNames: [],
    lastNames: [],
    emails: [],
    legalEntityCodes: [],
  });

  // State for UserDetail modal
  const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
  const [isUserDetailOpen, setIsUserDetailOpen] = useState(false);

  // Convert local filter state to API filter format
  const filters = useMemo<UserFilters>(
    () => ({
      // Map firstNames to names for API compatibility
      names: filterState.firstNames.length > 0 ? filterState.firstNames : undefined,
      emails: filterState.emails.length > 0 ? filterState.emails : undefined,
      // Map legalEntityCodes to billedEntities for API compatibility
      billedEntities: filterState.legalEntityCodes.length > 0 ? filterState.legalEntityCodes : undefined,
    }),
    [filterState]
  );

  // Track whether this is the initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Use the enhanced table hook
  const { pagination, sorting, handleSortingChange, onChangePage, onChangePageSize, getTextFieldProps, validateItem, clearValidationErrors } =
    useEnhancedTable<UserDto>({
      initialSorting: [
        {
          id: 'firstName',
          desc: false,
        },
      ],
      onSearchQueryChange: searchQuery,
      requiredFields: ['firstName', 'lastName', 'email'],
    });

  const { mutateAsync: upsertUser } = useUpsertUser();

  // Track sorting field and direction for API request
  const sortingField = sorting.length > 0 ? sorting[0].id : undefined;
  let sortingDirection: 'ASC' | 'DESC' | undefined;
  if (sorting.length > 0) {
    sortingDirection = sorting[0].desc ? 'DESC' : 'ASC';
  }

  // Fetch users data
  const { data, isLoading, refetch } = useUserList(
    {
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sortingColum: sortingField,
      sortingDirection,
    },
    filters
  );

  // Get metadata for filter options with current filters
  const { data: metadataData, refetch: refetchMetadata } = useUsersMetadata(filters);

  // Refetch data when filters change
  useEffect(() => {
    if (isInitialLoad) {
      // On initial load, don't trigger a refetch with empty filters
      setIsInitialLoad(false);
    } else {
      // For subsequent filter changes, refetch data
      refetch();
    }
  }, [filters, refetch, isInitialLoad, setIsInitialLoad]);

  // Memoize users to avoid recreating on every render
  const users = useMemo(() => {
    const content = data?.data?.content || [];
    return content;
  }, [data]);
  const usersTotal = data?.data?.totalElements || 0;

  // Function to update filter state
  const updateFilter = useCallback((columnId: string, selectedValues: string[]) => {
    setFilterState((prevFilters) => {
      const newFilters = { ...prevFilters };
      switch (columnId) {
        case 'firstName':
          newFilters.firstNames = selectedValues;
          break;
        case 'lastName':
          newFilters.lastNames = selectedValues;
          break;
        case 'email':
          newFilters.emails = selectedValues;
          break;
        case 'legalEntityCode':
          newFilters.legalEntityCodes = selectedValues;
          break;
        default:
          break;
      }
      return newFilters;
    });
  }, []);

  // Handler for table header sorting
  const handleTableSort = useCallback(
    (columnId: string, direction: 'asc' | 'desc') => {
      handleSortingChange([{ id: columnId, desc: direction === 'desc' }]);
    },
    [handleSortingChange]
  );

  // Handler for table filters
  const handleTableFilter = useCallback(
    (columnId: string, selectedValues: string[]) => {
      // If no values are selected or 'All' is selected, clear the filter
      const isAllSelected = selectedValues.some((value) => value === 'all');

      if (!selectedValues.length || isAllSelected) {
        // Clear the filter for this column
        switch (columnId) {
          case 'firstName':
            updateFilter('firstName', []);
            break;
          case 'lastName':
            updateFilter('lastName', []);
            break;
          case 'email':
            updateFilter('email', []);
            break;
          case 'legalEntityCode':
            updateFilter('legalEntityCode', []);
            break;
          default:
            break;
        }

        // Refetch data with cleared filter
        refetch();
        refetchMetadata();
        return;
      }

      // Apply the filter based on column ID
      switch (columnId) {
        case 'firstName':
          updateFilter('firstName', selectedValues);
          break;
        case 'lastName':
          updateFilter('lastName', selectedValues);
          break;
        case 'email':
          updateFilter('email', selectedValues);
          break;
        case 'legalEntityCode':
          updateFilter('legalEntityCode', selectedValues);
          break;
        default:
          break;
      }

      // Refetch data with new filter
      refetch();
      refetchMetadata();
    },
    [updateFilter, refetch, refetchMetadata]
  );

  const columns = useMemo(
    (): MRT_ColumnDef<UserDto>[] => [
      // ID column - hidden but available for reference
      {
        accessorKey: 'id',
        header: 'ID',
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: 'firstName',
        header: 'First Name',
        enableSorting: false,
        muiTableHeadCellProps: {
          sx: { width: '130px' },
        },
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.firstNames);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filterState.firstNames}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('firstName', { required: true }),
      },
      {
        accessorKey: 'lastName',
        header: 'Last Name',
        enableSorting: false,
        muiTableHeadCellProps: {
          sx: { width: '130px' },
        },
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.lastNames);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filterState.lastNames}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('lastName', { required: true }),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        enableSorting: false,
        muiTableHeadCellProps: {
          sx: { width: '180px' },
        },
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.emails);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filterState.emails}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('email', { required: true }),
      },
      {
        accessorFn: (row) => row.legalEntity?.code,
        header: 'Legal Entity',
        enableSorting: false,
        id: 'legalEntityCode',
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.legalEntityCodes);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filterState.legalEntityCodes}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('legalEntity', { required: false }),
      },
      {
        accessorKey: 'createdOn',
        header: 'Created On',
        enableSorting: false,
        enableEditing: false,
        Cell: ({ cell }) => {
          const date = cell.getValue() ? new Date(cell.getValue() as string) : null;
          return date
            ? date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : '';
        },
      },
    ],
    [
      getTextFieldProps,
      handleTableSort,
      handleTableFilter,
      metadataData?.data,
      filterState.firstNames,
      filterState.lastNames,
      filterState.emails,
      filterState.legalEntityCodes,
    ]
  );

  // Function to prepare the entity for upsert operation
  const prepareEntityForUpsert = useCallback((values: Record<string, any>, originalData?: any): UserDto => {
    // Extract all values directly
    const { id, firstName, lastName, email, active, roles } = values;

    const emptyToNull = (value: string | null | undefined): string | null => {
      if (value === undefined || value === null || value === '') {
        return null;
      }
      return value;
    };

    // Return the DTO with fields directly from form values
    const dto: UserDto = {
      id: originalData?.id || id || undefined,
      firstName: emptyToNull(firstName),
      lastName: emptyToNull(lastName),
      email: emptyToNull(email),
      active: active === 'true' || active === true,
      roles: roles || [],
      // Preserve other fields from original data if they exist
      ...(originalData && {
        middleName: originalData.middleName,
        cognitoId: originalData.cognitoId,
        legalEntity: originalData.legalEntity,
        userPermissionList: originalData.userPermissionList,
        createdOn: originalData.createdOn,
        updatedOn: originalData.updatedOn,
      }),
    };

    return dto;
  }, []);

  // Use the standardized entity handler hook
  const { handleCreate: handleCreateUser, handleUpdate: handleUpdateUser } = useTableEntityHandler<UserDto>(
    {
      entityName: 'User',
      upsertEntity: upsertUser,
      deleteEntity: async ({ id }: { id: string }) => {
        // Note: Delete functionality is skipped as requested
        // This is a placeholder for future implementation
        // eslint-disable-next-line no-console
        console.log('Delete user with ID:', id);
      },
      prepareEntityForUpsert,
    },
    validateItem
  );

  // Render row actions for users
  const renderRowActions = useCallback(
    ({ row, table }) => (
      <Box sx={{ display: 'flex' }}>
        <Tooltip title='Edit'>
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    []
  );

  // Handle opening the user detail modal
  const handleOpenUserDetail = useCallback((user: UserDto) => {
    setSelectedUser(user);
    setIsUserDetailOpen(true);
  }, []);

  // Handle closing the user detail modal
  const handleCloseUserDetail = useCallback(() => {
    setIsUserDetailOpen(false);
    setSelectedUser(null);
  }, []);

  // Update selectedUser when users data changes (e.g., after permission update)
  useEffect(() => {
    if (selectedUser && users.length > 0) {
      const updatedUser = users.find((user) => user.id === selectedUser.id);
      if (updatedUser) {
        // Only update if the user object reference is different (React Query gives us new objects when data changes)
        if (updatedUser !== selectedUser) {
          console.log('User data reference changed, updating selectedUser');
          setSelectedUser(updatedUser);
        }
      }
    }
  }, [users, selectedUser?.id]);

  const baseTableConfig = createTableConfig<UserDto>({
    hiddenColumns: ['id'],
    onCreatingCancel: clearValidationErrors,
    onEditingCancel: clearValidationErrors,
  });

  const table = useMaterialReactTable({
    ...baseTableConfig,
    onCreatingRowSave: handleCreateUser,
    onEditingRowSave: handleUpdateUser,
    data: users,
    columns,
    state: {
      sorting,
      isLoading,
    },
    enableRowActions: true,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: selectedUser?.id === row.original.id ? 'rgb(242, 242, 242)' : 'inherit',
        '&:hover': {
          backgroundColor: 'rgb(242, 242, 242)',
        },
      },
    }),
    muiTableBodyCellProps: ({ row, column }) => ({
      onClick: () => {
        if (column?.id !== 'mrt-row-actions') {
          handleOpenUserDetail(row.original);
        }
      },
      sx: {
        backgroundColor: selectedUser?.id === row.original.id ? 'rgb(242, 242, 242)' : 'inherit',
        '&:hover': {
          backgroundColor: 'rgb(242, 242, 242)',
        },
        cursor: column?.id !== 'mrt-row-actions' ? 'pointer' : 'default',
      },
    }),
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        muiTableHeadCellProps: {
          sx: { width: '80px' },
        },
      },
    },
    muiTableHeadProps: {
      sx: {
        '& th:first-child': firstCellStyles,
      },
    },
    muiTableBodyProps: {
      sx: {
        '& td:first-child': firstCellStyles,
      },
    },
    onSortingChange: (updatedSorting) => {
      if (typeof updatedSorting === 'function') {
        const newSortingState = updatedSorting(sorting);
        handleSortingChange(newSortingState);
      } else {
        handleSortingChange(updatedSorting);
      }
    },
    renderRowActions: ({ row, table }) => renderRowActions({ row, table }),
  });

  // Using BaseTableWithPagination to render the table
  return (
    <SettingsMain
      title='User Details'
      subtitle='Adjust the user permissions on the platform'
      backPath='/settings'
      topActions={
        <Button
          variant='primary'
          className='!pr-12 !pl-16 !ml-12'
          onClick={() => {
            table.setCreatingRow(true);
          }}
        >
          <div className='flex items-center gap-10'>
            <span className='text-14 xl:text-16'>Add New</span>
            <AddIcon />
          </div>
        </Button>
      }
    >
      <SettingsBaseTable table={table} totalItems={usersTotal} pagination={pagination} onChangePage={onChangePage} onChangePageSize={onChangePageSize} />

      {/* User Detail Modal */}
      {selectedUser && (
        <Slide direction='left' in={isUserDetailOpen} mountOnEnter unmountOnExit>
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '50%', zIndex: 10 }}>
            <UserDetail open={isUserDetailOpen} onClose={handleCloseUserDetail} user={selectedUser} />
          </div>
        </Slide>
      )}
    </SettingsMain>
  );
};
